import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Empty, Input, Spin } from "antd";
import { MagnifyingGlass } from "@phosphor-icons/react";

import {
  fetchHelpArticle,
  fetchHelpSections,
  searchHelp,
} from "../../service/help";
import HelpMarkdown from "./HelpMarkdown";
import {
  Article,
  ArticleSummary,
  Body,
  Breadcrumb,
  Card,
  CardGrid,
  CardLink,
  CardTitle,
  Centered,
  Content,
  Header,
  Landing,
  Nav,
  NavItem,
  NavSection,
  NavSectionTitle,
  Page,
  Pager,
  PagerLabel,
  PagerLink,
  PagerTitle,
  ResultRow,
  ResultSection,
  ResultSnippet,
  ResultTitle,
  Results,
  SearchWrap,
  Subtitle,
  Title,
} from "./HelpStyled";

// Long enough that someone typing a phrase is not searched on every
// keystroke, short enough that the list feels live.
const SEARCH_DEBOUNCE_MS = 220;
const MIN_QUERY_LENGTH = 2;

// The backend wraps matched terms in the search snippet rather than
// sending HTML, so the reader decides how to render a hit.
const HIGHLIGHT = /\[\[HIT\]\](.*?)\[\[\/HIT\]\]/g;

export const highlightSnippet = (snippet) => {
  if (!snippet) return null;
  const pieces = [];
  let cursor = 0;
  let match = HIGHLIGHT.exec(snippet);
  while (match !== null) {
    if (match.index > cursor) {
      pieces.push(snippet.slice(cursor, match.index));
    }
    pieces.push(<mark key={`${match.index}-${match[1]}`}>{match[1]}</mark>);
    cursor = match.index + match[0].length;
    match = HIGHLIGHT.exec(snippet);
  }
  HIGHLIGHT.lastIndex = 0;
  if (cursor < snippet.length) pieces.push(snippet.slice(cursor));
  return pieces;
};

const Help = () => {
  const navigate = useNavigate();
  const { "*": slug } = useParams();

  const [sections, setSections] = useState([]);
  const [sectionsLoading, setSectionsLoading] = useState(true);
  const [article, setArticle] = useState(null);
  const [articleLoading, setArticleLoading] = useState(false);
  const [articleMissing, setArticleMissing] = useState(false);

  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [resultsOpen, setResultsOpen] = useState(false);
  const [highlighted, setHighlighted] = useState(0);

  const searchWrapRef = useRef(null);
  const contentRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    fetchHelpSections()
      .then((response) => {
        if (!cancelled) setSections(response?.data?.sections || []);
      })
      .catch(() => {
        if (!cancelled) setSections([]);
      })
      .finally(() => {
        if (!cancelled) setSectionsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!slug) {
      setArticle(null);
      setArticleMissing(false);
      return undefined;
    }
    let cancelled = false;
    setArticleLoading(true);
    setArticleMissing(false);
    fetchHelpArticle(slug)
      .then((response) => {
        if (cancelled) return;
        setArticle(response?.data || null);
        // A cross-link from halfway down a long article should land the
        // reader at the top of the new one, not where they were.
        if (contentRef.current) contentRef.current.scrollTop = 0;
      })
      .catch(() => {
        if (cancelled) return;
        setArticle(null);
        setArticleMissing(true);
      })
      .finally(() => {
        if (!cancelled) setArticleLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [slug]);

  useEffect(() => {
    const trimmed = query.trim();
    if (trimmed.length < MIN_QUERY_LENGTH) {
      setResults([]);
      setResultsOpen(false);
      return undefined;
    }
    let cancelled = false;
    const timer = setTimeout(() => {
      searchHelp(trimmed)
        .then((response) => {
          if (cancelled) return;
          setResults(response?.data?.results || []);
          setResultsOpen(true);
          setHighlighted(0);
        })
        .catch(() => {
          if (!cancelled) setResults([]);
        });
    }, SEARCH_DEBOUNCE_MS);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [query]);

  useEffect(() => {
    const onDocumentClick = (event) => {
      if (
        searchWrapRef.current &&
        !searchWrapRef.current.contains(event.target)
      ) {
        setResultsOpen(false);
      }
    };
    document.addEventListener("mousedown", onDocumentClick);
    return () => document.removeEventListener("mousedown", onDocumentClick);
  }, []);

  const openArticle = useCallback(
    (articleSlug) => {
      setResultsOpen(false);
      setQuery("");
      navigate(`/help/${articleSlug}`);
    },
    [navigate]
  );

  const onSearchKeyDown = (event) => {
    if (!resultsOpen || results.length === 0) return;
    if (event.key === "ArrowDown") {
      event.preventDefault();
      setHighlighted((current) => (current + 1) % results.length);
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      setHighlighted((current) =>
        current === 0 ? results.length - 1 : current - 1
      );
    } else if (event.key === "Enter") {
      event.preventDefault();
      openArticle(results[highlighted].slug);
    } else if (event.key === "Escape") {
      setResultsOpen(false);
    }
  };

  const navigation = useMemo(
    () =>
      sections.map((section) => (
        <NavSection key={section.section}>
          <NavSectionTitle>{section.section}</NavSectionTitle>
          {section.articles.map((item) => (
            <NavItem
              key={item.slug}
              $active={item.slug === slug}
              onClick={() => navigate(`/help/${item.slug}`)}
            >
              {item.title}
            </NavItem>
          ))}
        </NavSection>
      )),
    [sections, slug, navigate]
  );

  const renderContent = () => {
    if (articleLoading) {
      return (
        <Centered>
          <Spin />
        </Centered>
      );
    }

    if (articleMissing) {
      return (
        <Empty
          description="That help page does not exist. Try searching for it above."
          style={{ padding: 64 }}
        />
      );
    }

    if (article) {
      return (
        <Article>
          <Breadcrumb>{article.section}</Breadcrumb>
          {article.summary ? (
            <ArticleSummary>{article.summary}</ArticleSummary>
          ) : null}
          <HelpMarkdown body={article.body_markdown} />
          {article.previous || article.next ? (
            <Pager>
              {article.previous ? (
                <PagerLink onClick={() => openArticle(article.previous.slug)}>
                  <PagerLabel>Previous</PagerLabel>
                  <PagerTitle>{article.previous.title}</PagerTitle>
                </PagerLink>
              ) : null}
              {article.next ? (
                <PagerLink $next onClick={() => openArticle(article.next.slug)}>
                  <PagerLabel>Next</PagerLabel>
                  <PagerTitle>{article.next.title}</PagerTitle>
                </PagerLink>
              ) : null}
            </Pager>
          ) : null}
        </Article>
      );
    }

    if (sectionsLoading) {
      return (
        <Centered>
          <Spin />
        </Centered>
      );
    }

    if (sections.length === 0) {
      return (
        <Empty
          description="No help articles have been published yet."
          style={{ padding: 64 }}
        />
      );
    }

    return (
      <Landing>
        <CardGrid>
          {sections.map((section) => (
            <Card key={section.section}>
              <CardTitle>{section.section}</CardTitle>
              {section.articles.map((item) => (
                <CardLink
                  key={item.slug}
                  onClick={() => navigate(`/help/${item.slug}`)}
                >
                  {item.title}
                </CardLink>
              ))}
            </Card>
          ))}
        </CardGrid>
      </Landing>
    );
  };

  return (
    <Page>
      <Header>
        <Title>Help</Title>
        <Subtitle>
          How to use WHG Docs — search for what you are trying to do, or browse
          by topic.
        </Subtitle>
        <SearchWrap ref={searchWrapRef}>
          <Input
            size="large"
            prefix={<MagnifyingGlass size={18} />}
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            onKeyDown={onSearchKeyDown}
            onFocus={() => {
              if (results.length > 0) setResultsOpen(true);
            }}
            placeholder='Search help, such as "send a document to the VA"'
            allowClear
            aria-label="Search help"
          />
          {resultsOpen ? (
            <Results role="listbox" aria-label="Help search results">
              {results.length === 0 ? (
                <ResultRow as="div">
                  <ResultSnippet>
                    Nothing matched “{query.trim()}”.
                  </ResultSnippet>
                </ResultRow>
              ) : (
                results.map((result, index) => (
                  <ResultRow
                    key={result.slug}
                    role="option"
                    aria-selected={index === highlighted}
                    $active={index === highlighted}
                    onMouseEnter={() => setHighlighted(index)}
                    onClick={() => openArticle(result.slug)}
                  >
                    <ResultTitle>
                      <span>{result.title}</span>
                      <ResultSection>{result.section}</ResultSection>
                    </ResultTitle>
                    <ResultSnippet>
                      {highlightSnippet(result.snippet) || result.summary}
                    </ResultSnippet>
                  </ResultRow>
                ))
              )}
            </Results>
          ) : null}
        </SearchWrap>
      </Header>

      <Body>
        <Nav>{navigation}</Nav>
        <Content ref={contentRef}>{renderContent()}</Content>
      </Body>
    </Page>
  );
};

export default Help;
